import React, { useState } from 'react';
import Modal from './Modal';
import { SUPPORT_EMAIL } from '../config';
import f from './forms.module.css';
import styles from './JoinModal.module.css';

const SERVE_AREAS = ['Worship', 'Ministry', 'Production', 'Hospitality', 'Events', 'Creative'];

const encode = (data) =>
  Object.keys(data)
    .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(data[k] || ''))
    .join('&');

export default function JoinModal({ onClose }) {
  const [step, setStep] = useState('intro');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    serveArea: '',
    inChurch: '',
    churchName: '',
    servesAtChurch: '',
    department: '',
    leaderContact: '',
    leaderName: '',
    leaderPhone: '',
    leaderEmail: '',
    eventsAttended: '',
    notes: '',
    'bot-field': '',
  });

  const update = (key, value) => setForm((s) => ({ ...s, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({
          'form-name': 'serve-application',
          name: form.name,
          email: form.email,
          phone: form.phone,
          serveArea: form.serveArea,
          inChurch: form.inChurch,
          churchName: form.churchName,
          servesAtChurch: form.servesAtChurch,
          department: form.department,
          leaderContact: form.leaderContact,
          leaderName: form.leaderName,
          leaderPhone: form.leaderPhone,
          leaderEmail: form.leaderEmail,
          eventsAttended: form.eventsAttended,
          notes: form.notes,
          'bot-field': form['bot-field'],
        }),
      });
      if (!response.ok) throw new Error('Submission failed');
      setStep('done');
    } catch (err) {
      setError(`Something went wrong. Please try again, or email us at ${SUPPORT_EMAIL}.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open onClose={onClose} label="Serve with Revive" size="lg">
      {step === 'intro' && (
        <>
          <span className={f.label}>join us</span>
          <h2 className={f.title}>
            Serve with <em>Revive.</em>
          </h2>
          <p className={f.body}>
            We appreciate and welcome your interest in sharing your gifts with us.
            We have a few expectations for those who serve with Revive, and it's
            totally okay if you're still working toward meeting these.
          </p>

          <ul className={styles.checklist}>
            <li>
              <span className={styles.checklistDot} />
              You're an active follower of Jesus, with a walk that reflects it.
            </li>
            <li>
              <span className={styles.checklistDot} />
              You're plugged into a local church (or actively pursuing one).
            </li>
            <li>
              <span className={styles.checklistDot} />
              You've attended at least three Revive events.
            </li>
          </ul>

          <p className={f.helper} style={{ maxWidth: 540 }}>
            These expectations aren't here to gatekeep, they help us steward
            Revive well. If that sounds like you, continue below.
          </p>

          <button className={f.primary} onClick={() => setStep('form')}>
            continue
            <span aria-hidden="true">→</span>
          </button>
        </>
      )}

      {step === 'form' && (
        <form
          onSubmit={handleSubmit}
          name="serve-application"
          data-netlify="true"
          data-netlify-honeypot="bot-field"
        >
          <input type="hidden" name="form-name" value="serve-application" />
          <p hidden>
            <label>
              Don't fill this out:
              <input
                name="bot-field"
                value={form['bot-field']}
                onChange={(e) => update('bot-field', e.target.value)}
              />
            </label>
          </p>

          <span className={f.label}>a few questions</span>
          <h2 className={f.titleSm}>Tell us about you.</h2>

          <div className={f.field}>
            <label className={f.fieldLabel}>name</label>
            <input
              className={f.input}
              type="text"
              name="name"
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              required
            />
          </div>

          <div className={f.field}>
            <label className={f.fieldLabel}>email</label>
            <input
              className={f.input}
              type="email"
              name="email"
              value={form.email}
              onChange={(e) => update('email', e.target.value)}
              required
            />
          </div>

          <div className={f.field}>
            <label className={f.fieldLabel}>phone</label>
            <input
              className={f.input}
              type="tel"
              name="phone"
              placeholder="(555) 123-4567"
              value={form.phone}
              onChange={(e) => update('phone', e.target.value)}
              required
            />
          </div>

          <div className={f.field}>
            <label className={f.fieldLabel}>where would you like to serve?</label>
            <div className={f.chips}>
              {SERVE_AREAS.map((area) => (
                <button
                  key={area}
                  type="button"
                  className={`${f.chip} ${form.serveArea === area ? f.chipActive : ''}`}
                  onClick={() => update('serveArea', area)}
                >
                  {area}
                </button>
              ))}
            </div>
            <input type="hidden" name="serveArea" value={form.serveArea} />
          </div>

          <div className={f.field}>
            <label className={f.fieldLabel}>are you part of a local church?</label>
            <div className={f.radioGroup}>
              {['Yes', 'No', 'Looking for one'].map((opt) => (
                <label key={opt} className={f.radio}>
                  <input
                    type="radio"
                    name="inChurch"
                    value={opt}
                    checked={form.inChurch === opt}
                    onChange={(e) => update('inChurch', e.target.value)}
                  />
                  <span>{opt}</span>
                </label>
              ))}
            </div>
          </div>

          {form.inChurch === 'Yes' && (
            <div className={f.field}>
              <label className={f.fieldLabel}>which church?</label>
              <input
                className={f.input}
                type="text"
                name="churchName"
                value={form.churchName}
                onChange={(e) => update('churchName', e.target.value)}
              />
            </div>
          )}

          <div className={f.field}>
            <label className={f.fieldLabel}>do you currently serve at your church?</label>
            <div className={f.radioGroup}>
              {['Yes', 'No'].map((opt) => (
                <label key={opt} className={f.radio}>
                  <input
                    type="radio"
                    name="servesAtChurch"
                    value={opt}
                    checked={form.servesAtChurch === opt}
                    onChange={(e) => update('servesAtChurch', e.target.value)}
                  />
                  <span>{opt}</span>
                </label>
              ))}
            </div>
          </div>

          {form.servesAtChurch === 'Yes' && (
            <div className={f.field}>
              <label className={f.fieldLabel}>what department?</label>
              <input
                className={f.input}
                type="text"
                name="department"
                placeholder="e.g. worship, production, greeting"
                value={form.department}
                onChange={(e) => update('department', e.target.value)}
              />
            </div>
          )}

          {form.servesAtChurch === 'Yes' && (
            <div className={f.field}>
              <label className={f.fieldLabel}>are you ok with us reaching out to your team leader?</label>
              <div className={f.radioGroup}>
                {['Yes', 'No'].map((opt) => (
                  <label key={opt} className={f.radio}>
                    <input
                      type="radio"
                      name="leaderContact"
                      value={opt}
                      checked={form.leaderContact === opt}
                      onChange={(e) => update('leaderContact', e.target.value)}
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {form.servesAtChurch === 'Yes' && form.leaderContact === 'Yes' && (
            <>
              <div className={f.field}>
                <label className={f.fieldLabel}>team leader's name</label>
                <input
                  className={f.input}
                  type="text"
                  name="leaderName"
                  value={form.leaderName}
                  onChange={(e) => update('leaderName', e.target.value)}
                />
              </div>
              <div className={f.field}>
                <label className={f.fieldLabel}>team leader's phone</label>
                <input
                  className={f.input}
                  type="tel"
                  name="leaderPhone"
                  value={form.leaderPhone}
                  onChange={(e) => update('leaderPhone', e.target.value)}
                />
              </div>
              <div className={f.field}>
                <label className={f.fieldLabel}>team leader's email</label>
                <input
                  className={f.input}
                  type="email"
                  name="leaderEmail"
                  value={form.leaderEmail}
                  onChange={(e) => update('leaderEmail', e.target.value)}
                />
              </div>
            </>
          )}

          <div className={f.field}>
            <label className={f.fieldLabel}>how many revive events have you attended?</label>
            <input
              className={f.input}
              type="text"
              name="eventsAttended"
              placeholder="e.g. 3, or names of events"
              value={form.eventsAttended}
              onChange={(e) => update('eventsAttended', e.target.value)}
            />
          </div>

          <div className={f.field}>
            <label className={f.fieldLabel}>anything else? (optional)</label>
            <textarea
              className={f.textarea}
              name="notes"
              rows={3}
              value={form.notes}
              onChange={(e) => update('notes', e.target.value)}
            />
          </div>

          {error && <p className={f.errorMsg}>{error}</p>}

          <button type="submit" className={f.primary} disabled={submitting}>
            {submitting ? 'sending…' : 'send application'}
            <span aria-hidden="true">→</span>
          </button>
        </form>
      )}

      {step === 'done' && (
        <>
          <span className={f.label}>received</span>
          <h2 className={f.title}>Thank you.</h2>
          <p className={f.body}>
            Your application is on its way to our team. We'll review and reach
            out soon. In the meantime, see you at the next gathering.
          </p>
          <button className={f.primary} onClick={onClose}>close</button>
        </>
      )}
    </Modal>
  );
}
